<template>
	<div>
		<div class="col-md-12">
			<div class="row">
				<div class="col-md-5">
					<h3 class="page-header">
						<i class="fa fa-inr animated bounceInDown show-info"></i>
						{{ run ? monthLabel(run.cycle_label) : 'Payroll' }}
						<span v-if="run" :class="runStatusClass(run.status)" style="margin-left:8px">
							{{ runStatusLabel(run.status) }}
						</span>
						<!-- A run left open after approving some people says how far it has got. -->
						<span v-if="isOpen && approvedCount" class="badge bg-primary" style="margin-left:4px">
							Approved {{ approvedCount }} of {{ table.length }}
						</span>
					</h3>
				</div>
				<div class="col-md-7 text-right run-actions" style="margin-top:20px">
					<button class="btn btn-default" @click="back">Back</button>
					<button
						v-if="canSelect"
						class="btn btn-success"
						style="margin-left:6px"
						@click="compute(selected.slice())"
						:disabled="busy || !selected.length"
					>
						<i class="fa fa-file-text-o"></i>
						{{ run.status === 'draft' ? 'Compute' : 'Recompute' }} selected ({{ selected.length }})
					</button>
					<button
						v-if="isOpen"
						class="btn btn-info"
						style="margin-left:6px"
						@click="compute()"
						:disabled="busy"
					>
						<i class="fa fa-refresh"></i>
						{{ busy ? 'Computing…' : run.status === 'draft' ? 'Compute all' : 'Recompute all' }}
					</button>
					<button
						v-if="canSelect && run.status === 'computed'"
						class="btn btn-primary"
						style="margin-left:6px"
						@click="approve(selectedDrafts)"
						:disabled="busy || !selectedDrafts.length"
					>
						<i class="fa fa-check"></i> Approve selected ({{ selectedDrafts.length }})
					</button>
					<button
						v-if="run && run.status === 'computed'"
						class="btn btn-primary"
						style="margin-left:6px"
						@click="approve()"
						:disabled="busy"
					>
						<i class="fa fa-check-square-o"></i> Approve all ({{ draftCount }})
					</button>
					<button
						v-if="reopenableRows.length"
						class="btn btn-warning"
						style="margin-left:6px"
						@click="reopen()"
						:disabled="busy"
					>
						<i class="fa fa-undo"></i> Reopen approved ({{ reopenableRows.length }})
					</button>
				</div>
			</div>
		</div>

		<div class="col-md-12">
			<div v-if="error" class="alert alert-danger">{{ error }}</div>

			<!--
				The server refuses to approve an unlocked period. Saying so before
				the button is pressed is kinder than a validation error after.
			-->
			<div v-if="run && run.status === 'computed' && !run.att_lock_id" class="alert alert-warning">
				<strong>Attendance is not locked for this period.</strong>
				These figures can still move. Lock
				{{ formatDate(run.period_start) }} – {{ formatDate(run.period_end) }}
				on the attendance report before approving.
			</div>

			<div v-if="notes.length" class="alert alert-warning">
				<div v-for="(note, i) in notes" :key="i">{{ note }}</div>
			</div>

			<!-- Generating only drafts; people look for them in the ledger and find nothing. -->
			<p v-if="isOpen && rows.length" class="text-muted" style="font-size:12px">
				Generated slips are drafts. They reach the employee ledger (Payroll → Ledger) only when this
				run is approved.
			</p>
		</div>

		<div class="col-md-12" v-if="run">
			<div class="row stock_section">
				<div class="col-md-3">
					<label>Period</label>
					<div>{{ formatDate(run.period_start) }} – {{ formatDate(run.period_end) }}</div>
				</div>
				<div class="col-md-2">
					<label>Employees</label>
					<div>{{ run.employee_count }}</div>
				</div>
				<div class="col-md-2">
					<label>Gross</label>
					<div>{{ moneyShort(run.total_gross) }}</div>
				</div>
				<div class="col-md-2">
					<label>Deductions</label>
					<div>{{ moneyShort(run.total_deductions) }}</div>
				</div>
				<div class="col-md-3">
					<label>Net payable</label>
					<div><strong>{{ money(run.total_net) }}</strong></div>
				</div>
			</div>
		</div>

		<div class="col-md-12">
			<div class="panel">
				<div class="panel-body" style="background:none">
					<div v-if="loading" class="text-muted">Loading…</div>

					<table v-else class="table table-bordered table-hover table-striped display">
						<thead class="table-font" style="background:#475c78;color:#fff">
							<th v-if="canSelect" style="width:30px">
								<input
									type="checkbox"
									title="Select all"
									:checked="allSelected"
									:disabled="!selectableIds.length"
									@change="toggleAll($event.target.checked)"
								/>
							</th>
							<th>#</th>
							<th>Employee</th>
							<th class="text-right">Payable days</th>
							<th class="text-right">LOP</th>
							<th class="text-right">Basic</th>
							<th class="text-right">Fuel</th>
							<th class="text-right">Gross</th>
							<th class="text-right">Deductions</th>
							<th class="text-right">Net</th>
							<th class="text-right">Recovered</th>
							<th class="text-right">Payable</th>
							<th class="text-right">vs last</th>
							<th>Status</th>
							<th></th>
						</thead>
						<tbody class="table-font">
							<tr
								v-for="(row, index) in table"
								:key="row.employee_id"
								:class="{ 'just-generated': justGenerated.includes(row.employee_id) }"
							>
								<td v-if="canSelect">
									<!-- An approved slip can never change, so it cannot be picked. -->
									<input
										type="checkbox"
										:value="row.employee_id"
										v-model="selected"
										:disabled="!isSelectable(row)"
										:title="isSelectable(row) ? '' : 'Approved - cannot be changed'"
									/>
								</td>
								<td>{{ index + 1 }}</td>
								<td>
									{{ row.name }}
									<div class="text-muted" style="font-size:11px">
										{{ row.employee_code || '' }}
										<span v-if="row.slip_no">· {{ row.slip_no }}</span>
									</div>
								</td>
								<template v-if="!row.slip_id">
									<td colspan="10" class="text-muted">—</td>
									<td><span class="badge bg-secondary">Not generated</span></td>
									<td></td>
								</template>
								<template v-else>
									<td class="text-right">{{ days(row.payable_days) }}</td>
									<td class="text-right" :class="{ 'text-danger': row.lop_days > 0 }">
										{{ days(row.lop_days) }}
									</td>
									<!-- The monthly figure, with what was paid under it when LOP cut it. -->
									<td class="text-right">
										{{ moneyShort(monthlyOr(row.basic_monthly, row.basic)) }}
										<div v-if="paidDiffers(row.basic_monthly, row.basic)" class="text-muted" style="font-size:11px">
											paid {{ moneyShort(row.basic) }}
										</div>
									</td>
									<td class="text-right">
										{{ moneyShort(monthlyOr(row.fuel_monthly, row.fuel)) }}
										<div v-if="paidDiffers(row.fuel_monthly, row.fuel)" class="text-muted" style="font-size:11px">
											paid {{ moneyShort(row.fuel) }}
										</div>
									</td>
									<td class="text-right">{{ moneyShort(row.gross_earnings) }}</td>
									<td class="text-right">{{ moneyShort(row.total_deductions) }}</td>
									<td class="text-right">{{ moneyShort(row.net_payable) }}</td>
									<td class="text-right">{{ moneyShort(row.total_recoveries) }}</td>
									<td class="text-right"><strong>{{ moneyShort(row.amount_payable) }}</strong></td>
									<!--
										The column that catches mistakes. A person whose pay
										moved 40% since last month is either a genuine change
										someone can name, or an error worth finding now.
									-->
									<td class="text-right" :class="varianceClass(variance(row.net_payable, row.previous_net))">
										<span v-if="variance(row.net_payable, row.previous_net) === null">—</span>
										<span v-else>
											{{ variance(row.net_payable, row.previous_net) > 0 ? '+' : ''
											}}{{ variance(row.net_payable, row.previous_net).toFixed(0) }}%
										</span>
									</td>
									<td>
										<span :class="slipStatusClass(row.status)">{{ slipStatusLabel(row.status) }}</span>
										<!-- Typed figures. Compute asks before rebuilding these. -->
										<span v-if="row.edited_at" class="badge bg-warning text-dark" style="margin-left:3px">Edited</span>
										<div v-if="row.computed_at" class="text-muted" style="font-size:11px">
											{{ generatedAt(row.computed_at) }}
										</div>
										<!-- Reopened drafts keep their old figures until recomputed; the server refuses to approve them. -->
										<div v-else-if="row.status === 'draft'" class="text-warning" style="font-size:11px">
											Reopened · recompute before approving
										</div>
									</td>
									<td style="white-space:nowrap">
										<button class="badge bg-primary" @click="openSlip(row)">Slip</button>
										<button
											v-if="isReopenable(row)"
											class="badge bg-warning text-dark"
											style="margin-left:4px"
											:disabled="busy"
											@click="reopen([row.employee_id])"
										>
											Reopen
										</button>
									</td>
								</template>
							</tr>
							<tr v-if="!table.length">
								<td :colspan="canSelect ? 15 : 14" class="text-center text-muted" style="padding:24px">
									No employees in this run. Enrol them under Attendance → Settings → Employees.
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<!--
			Compute would rebuild a hand-edited slip from attendance and the salary
			structure, throwing the typed figures away. Nobody loses them without
			being asked, one person at a time.
		-->
		<modal name="payroll-edits" height="auto" :adaptive="true" width="520">
			<div style="padding:20px">
				<h4 class="modal-header-title">These slips were edited by hand</h4>
				<p class="text-muted">
					Tick anyone whose typed figures should be thrown away and rebuilt from attendance. Everyone
					left unticked is skipped by this compute and keeps exactly what they have.
				</p>
				<table class="table table-striped">
					<tbody>
						<tr v-for="row in editedRows" :key="row.employee_id">
							<td style="width:30px">
								<input type="checkbox" :value="row.employee_id" v-model="discardFor" />
							</td>
							<td>
								{{ row.name }}
								<div class="text-muted" style="font-size:11px">
									Edited {{ generatedAt(row.edited_at).replace('Generated ', '') }}
								</div>
							</td>
							<td class="text-right">{{ moneyShort(row.net_payable) }}</td>
						</tr>
					</tbody>
				</table>
				<div class="text-right">
					<button class="btn btn-default" @click="$modal.hide('payroll-edits')">Cancel</button>
					<button class="btn btn-info" style="margin-left:6px" @click="confirmCompute">
						{{ discardFor.length ? 'Rebuild ' + discardFor.length + ', keep the rest' : 'Keep every edit' }}
					</button>
				</div>
			</div>
		</modal>

		<!-- Reversal touches the ledger for every slip; nothing on the page may be pressed until it is done. -->
		<div v-if="reopening" class="reopen-overlay">
			<div class="reopen-box">
				<i class="fa fa-spinner fa-spin fa-2x"></i>
				<div style="margin-top:12px"><strong>Reopening…</strong></div>
				<div class="text-muted" style="font-size:12px">Reversing payments and ledger entries. Please wait.</div>
			</div>
		</div>
	</div>
</template>

<script>
import { runService, errorMessage } from './payrollService'
import payrollFormat from './payrollFormat'

/**
 * Everyone in one period, with a variance column against the last one.
 *
 * Compute is idempotent on the server, so pressing it again is always safe:
 * draft slips are rebuilt and any advance they had recovered is reversed first.
 */
export default {
	name: 'PayrollRunDetail',
	mixins: [payrollFormat],
	data() {
		return {
			run: null,
			rows: [],
			employees: [],
			selected: [],
			// Set from the response: a server without `employees` ignores a
			// selection and recomputes everyone, so the checkboxes stay hidden.
			selectionSupported: false,
			justGenerated: [],
			notes: [],
			// The compute waiting on the keep/discard answer.
			pendingCompute: null,
			editedRows: [],
			discardFor: [],
			loading: false,
			busy: false,
			reopening: false,
			error: '',
		}
	},
	computed: {
		/** Slips can only be (re)generated before the run is approved. */
		isOpen() {
			return !!this.run && (this.run.status === 'draft' || this.run.status === 'computed')
		},
		canSelect() {
			return this.isOpen && this.selectionSupported
		},
		/**
		 * One row per person: their slip if it has been generated, otherwise a
		 * placeholder so they can still be selected. Once approved, only slips.
		 */
		table() {
			if (!this.canSelect) return this.rows
			const slips = {}
			this.rows.forEach((row) => {
				slips[row.employee_id] = row
			})
			const table = this.employees.map((e) => slips[e.employee_id] || { ...e, slip_id: null })
			const listed = new Set(this.employees.map((e) => e.employee_id))
			return table.concat(this.rows.filter((row) => !listed.has(row.employee_id)))
		},
		/** Everyone whose slip can still change: not generated yet, or a draft. */
		selectableIds() {
			return this.table.filter((row) => this.isSelectable(row)).map((row) => row.employee_id)
		},
		allSelected() {
			return this.selectableIds.length > 0 && this.selectableIds.every((id) => this.selected.includes(id))
		},
		/** The selected people who have a draft slip - the only ones that can be approved. */
		selectedDrafts() {
			return this.table
				.filter((row) => row.slip_id && row.status === 'draft' && this.selected.includes(row.employee_id))
				.map((row) => row.employee_id)
		},
		/** Drafts waiting for approval - what Approve all would finalise. */
		draftCount() {
			return this.rows.filter((row) => row.status === 'draft').length
		},
		approvedCount() {
			return this.rows.filter((row) => row.status === 'final' || row.status === 'paid').length
		},
		/** Approved, paid or cancelled slips - what Reopen approved would take back to draft. */
		reopenableRows() {
			return this.rows.filter((row) => this.isReopenable(row))
		},
	},
	mounted() {
		this.refresh()
	},
	methods: {
		refresh() {
			this.loading = true
			this.error = ''
			runService
				.show(this.$route.params.id)
				.then((data) => this.apply(data))
				.catch((e) => {
					this.error = errorMessage(e, 'Could not load that run.')
				})
				.finally(() => {
					this.loading = false
				})
		},
		apply(data) {
			this.run = data.run
			this.rows = data.rows || []
			this.employees = data.employees || []
			this.selectionSupported = Array.isArray(data.employees)
			this.notes = data.notes || []
		},
		/** An older server sends only what was paid; show that rather than nothing. */
		monthlyOr(monthly, paid) {
			return monthly === undefined || monthly === null ? paid : monthly
		},
		paidDiffers(monthly, paid) {
			return monthly !== undefined && monthly !== null && Math.round(monthly) !== Math.round(paid)
		},
		generatedAt(value) {
			const date = new Date(value)
			if (Number.isNaN(date.getTime())) return ''
			return (
				'Generated ' +
				date.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
			)
		},
		isSelectable(row) {
			return !row.slip_id || row.status === 'draft'
		},
		toggleAll(checked) {
			this.selected = checked ? this.selectableIds.slice() : []
		},
		/**
		 * Everyone when employeeIds is omitted; otherwise only those slips.
		 *
		 * Hand-edited slips are asked about first: the server keeps them unless
		 * it is told otherwise, so an unanswered dialog can never lose anything.
		 */
		compute(employeeIds) {
			const edited = this.rows.filter(
				(row) => row.edited_at && (!employeeIds || employeeIds.includes(row.employee_id))
			)

			if (edited.length) {
				this.editedRows = edited
				this.discardFor = []
				this.pendingCompute = employeeIds || null
				this.$modal.show('payroll-edits')
				return
			}

			this.runCompute(employeeIds)
		},
		confirmCompute() {
			this.$modal.hide('payroll-edits')
			this.runCompute(this.pendingCompute, this.discardFor.slice())
			this.pendingCompute = null
		},
		runCompute(employeeIds, discardEditsFor) {
			this.busy = true
			this.error = ''
			runService
				.compute(this.$route.params.id, employeeIds, discardEditsFor)
				.then((data) => {
					this.apply(data)
					this.selected = []
					// Highlight what was just rebuilt; someone skipped (no pay scale)
					// has no slip, and the warning above names them.
					const done = this.rows.map((row) => row.employee_id)
					this.justGenerated = (employeeIds || done).filter((id) => done.includes(id))
					const summary = employeeIds
						? `Computed ${this.justGenerated.length} of ${employeeIds.length} selected`
						: 'Computed'
					// Drafts on an unlocked period cannot be approved or reach the
					// ledger, so this is not a success yet — say what is missing.
					if (!this.run.att_lock_id) {
						this.$notify({
							title: 'Attendance is not locked',
							text:
								`${summary} as drafts only. Lock ${this.formatDate(this.run.period_start)} – ` +
								`${this.formatDate(this.run.period_end)} on the attendance report before approving.`,
							type: 'warn',
							duration: 8000,
						})
					} else {
						this.$notify({ text: `${summary}.`, type: 'success' })
					}
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not compute this run.')
				})
				.finally(() => {
					this.busy = false
				})
		},
		/**
		 * From here slips are immutable — a correction is an arrear next month.
		 * Everyone (and the run closes) when employeeIds is omitted; otherwise
		 * only those drafts, and the run stays open for the rest.
		 */
		approve(employeeIds) {
			const skipped = employeeIds ? this.selected.length - employeeIds.length : 0
			const message = employeeIds
				? `Approve ${employeeIds.length} selected slip(s)? They are numbered and posted to each ` +
				  "employee's ledger, and can never be edited again. The rest of the run stays open." +
				  (skipped ? `\n\n${skipped} selected without a generated slip will be skipped.` : '')
				: 'Approve every remaining slip and close this run? Slips are numbered and posted to each ' +
				  'employee ledger, and can never be edited again. Nothing more can be computed for this month.'
			if (!window.confirm(message)) return

			this.busy = true
			this.error = ''
			runService
				.approve(this.$route.params.id, employeeIds)
				.then((data) => {
					this.apply(data)
					this.selected = []
					this.justGenerated = []
					this.$notify({
						text:
							this.run.status === 'approved'
								? 'Approved. The run is closed.'
								: `Approved ${employeeIds.length}. The run stays open for the rest.`,
						type: 'success',
					})
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not approve this run.')
				})
				.finally(() => {
					this.busy = false
				})
		},
		isReopenable(row) {
			return !!row.slip_id && ['final', 'paid', 'cancelled'].includes(row.status)
		},
		/**
		 * Undo an approval - every approved slip when employeeIds is omitted.
		 * The server reverses each one's payments and ledger credit before
		 * answering, so the overlay stays up until that has happened.
		 */
		reopen(employeeIds) {
			const rows = employeeIds
				? this.reopenableRows.filter((row) => employeeIds.includes(row.employee_id))
				: this.reopenableRows
			const paid = rows.filter((row) => Number(row.paid_amount) > 0)
			const who = rows.length === 1 ? `${rows[0].name}'s slip` : `${rows.length} slips`
			const reason = window.prompt(
				`Reopen ${who}? Each goes back to draft and its salary is reversed in the employee ledger.` +
					(paid.length
						? `\n\nPayments of ${this.money(paid.reduce((sum, row) => sum + Number(row.paid_amount), 0))} ` +
						  `against ${paid.length} slip(s) will also be reversed. Record them again after re-approving.`
						: '') +
					'\n\nAfterwards recompute, then approve again.\n\nReason (required):'
			)
			if (reason === null) return
			if (!reason.trim()) {
				this.$notify({ text: 'A reason is required to reopen.', type: 'warn' })
				return
			}

			this.busy = true
			this.reopening = true
			this.error = ''
			runService
				.reopen(this.$route.params.id, reason.trim(), employeeIds)
				.then((data) => {
					this.apply(data)
					this.selected = []
					this.justGenerated = []
					this.$notify({
						text: `Reopened ${rows.length} slip(s). Recompute, then approve again.`,
						type: 'success',
					})
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not reopen this run.')
				})
				.finally(() => {
					this.busy = false
					this.reopening = false
				})
		},
		openSlip(row) {
			this.$router.push({ name: 'payroll-slip', params: { id: row.slip_id } })
		},
		back() {
			this.$router.push({ name: 'payroll-runs' })
		},
	},
}
</script>

<style scoped>
.text-right {
	text-align: right;
}
.just-generated td {
	background: #e8f6ec !important;
}
.reopen-overlay {
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	z-index: 2000;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(255, 255, 255, 0.75);
}
.reopen-box {
	text-align: center;
	background: #fff;
	border: 1px solid #ddd;
	border-radius: 6px;
	padding: 24px 32px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}
/*
 * The app pins .btn-primary to 87x30 with 4px padding, which wrapped the
 * Approve labels out of their buttons. Size every action to its text, alike.
 */
.run-actions .btn {
	white-space: nowrap;
	width: auto;
	height: auto;
	padding: 6px 12px !important;
	font-size: 16px;
	margin-right: 0;
	margin-bottom: 6px;
}
</style>
